import { useState, useEffect, useRef } from 'react';
import { useParams } from 'react-router-dom';
import { supabase } from '../../lib/supabase';
import { SITE_LOGO } from '../../constants/assets';

const JSPDF_SCRIPT = "https://cdnjs.cloudflare.com/ajax/libs/jspdf/2.5.1/jspdf.umd.min.js";
const HTML2CANVAS_SCRIPT = "https://cdnjs.cloudflare.com/ajax/libs/html2canvas/1.4.1/html2canvas.min.js";

interface Alteration {
  id: string;
  client_name: string;
  client_phone: string;
  service_name: string;
  description: string;
  price: number;
  created_at: string;
  partners: {
    name: string;
  };
}

export default function ReceiptView() {
  const { id } = useParams<{ id: string }>();
  const [alteration, setAlteration] = useState<Alteration | null>(null);
  const [loading, setLoading] = useState(true);
  const [pdfGenerating, setPdfGenerating] = useState(false);
  const [imgGenerating, setImgGenerating] = useState(false);
  const [shareImageUrl, setShareImageUrl] = useState<string | null>(null);
  const [shareFile, setShareFile] = useState<File | null>(null);
  const [libsReady, setLibsReady] = useState(false);
  const receiptRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    fetchData();
    const loadScript = (src: string, id: string) => {
      return new Promise((resolve) => {
        if (document.getElementById(id)) { resolve(true); return; }
        const script = document.createElement('script');
        script.id = id;
        script.src = src;
        script.onload = () => resolve(true);
        document.body.appendChild(script);
      });
    };
    const init = async () => {
      await loadScript(HTML2CANVAS_SCRIPT, 'canvas-lib');
      await loadScript(JSPDF_SCRIPT, 'pdf-lib');
      setLibsReady(true);
    };
    init();
  }, [id]);

  const fetchData = async () => {
    const { data } = await supabase
      .from('alterations')
      .select('*, partners(name)')
      .eq('id', id)
      .single();
      
    if (data) setAlteration(data);
    setLoading(false);
  };

  const handleDownloadPDF = async () => {
    if (!receiptRef.current || !libsReady || pdfGenerating) return;
    setPdfGenerating(true);
    try {
      const element = receiptRef.current;
      // Fuerza texto negro durante la captura (el térmico no rinde los grises claros)
      element.classList.add('receipt-thermal');
      const canvas = await (window as any).html2canvas(element, { scale: 3, useCORS: true, backgroundColor: '#FFFFFF' });
      element.classList.remove('receipt-thermal');
      const imgData = canvas.toDataURL('image/jpeg', 0.92);
      const { jsPDF } = (window as any).jspdf;

      // Formato Ticket térmico 58mm, alto dinámico, con margen de aire lateral
      const pdfWidth = 58;
      const sideMargin = 4;                     // margen de aire a cada lado (mm)
      const contentWidth = pdfWidth - sideMargin * 2;
      const contentHeight = (canvas.height * contentWidth) / canvas.width;
      const pdfHeight = contentHeight + sideMargin * 2; // margen de aire arriba y abajo

      const pdf = new jsPDF({ orientation: 'p', unit: 'mm', format: [pdfWidth, pdfHeight], compress: true });
      pdf.addImage(imgData, 'JPEG', sideMargin, sideMargin, contentWidth, contentHeight, undefined, 'FAST');
      pdf.save(`Ticket_Arreglo_${alteration?.id.split('-')[0].toUpperCase()}.pdf`);
    } catch (error) {
      console.error(error);
      receiptRef.current?.classList.remove('receipt-thermal');
    } finally {
      setPdfGenerating(false);
    }
  };

  // Genera el ticket como imagen PNG (58mm) para imprimir desde la app POS-Printer,
  // que imprime "imágenes de la galería". Abre un modal para guardarla/enviarla.
  const handleGenerateImage = async () => {
    if (!receiptRef.current || !libsReady || imgGenerating) return;
    setImgGenerating(true);
    try {
      const element = receiptRef.current;
      element.classList.add('receipt-thermal', 'receipt-compact');
      const canvas = await (window as any).html2canvas(element, { scale: 4, useCORS: true, backgroundColor: '#FFFFFF' });
      element.classList.remove('receipt-thermal', 'receipt-compact');
      const blob: Blob = await new Promise((resolve) => canvas.toBlob((b: Blob | null) => resolve(b as Blob), 'image/png'));
      const fileName = `Ticket_${alteration?.id.split('-')[0].toUpperCase()}.png`;
      setShareFile(new File([blob], fileName, { type: 'image/png' }));
      setShareImageUrl(URL.createObjectURL(blob));
    } catch (error) {
      console.error(error);
      receiptRef.current?.classList.remove('receipt-thermal');
    } finally {
      setImgGenerating(false);
    }
  };

  // Al pulsar: comparte el archivo ya generado (gesto síncrono → válido en iOS).
  // Ofrece "Guardar Imagen" (a Fotos) o enviar directo a la app de impresora.
  const handleShareFile = async () => {
    if (!shareFile) return;
    const nav = navigator as any;
    if (nav.canShare && nav.canShare({ files: [shareFile] })) {
      try { await nav.share({ files: [shareFile] }); } catch (e) { /* cancelado por el usuario */ }
    } else if (shareImageUrl) {
      const a = document.createElement('a');
      a.href = shareImageUrl;
      a.download = shareFile.name;
      a.click();
    }
  };

  const closeShareModal = () => {
    if (shareImageUrl) URL.revokeObjectURL(shareImageUrl);
    setShareImageUrl(null);
    setShareFile(null);
  };

  if (loading || !alteration) return (
    <div className="min-h-screen flex items-center justify-center bg-[#FDFDFD]">
      <i className="ri-loader-4-line animate-spin text-3xl text-brand-gray-400" />
    </div>
  );

  return (
    <div className="min-h-screen bg-[#FDFDFD] py-12 px-6 font-sans">
      <div className="max-w-[400px] mx-auto">
        {/* Elegant Action Bar */}
        <div className="flex justify-between items-center mb-8 no-print border-b border-brand-gray-100 pb-6">
          <button onClick={() => window.history.back()} className="text-brand-gray-400 hover:text-black transition-colors">
            <i className="ri-arrow-left-line text-xl" />
          </button>
          <div className="flex flex-wrap gap-2 justify-end">
            <button onClick={() => window.print()} className="text-[10px] uppercase tracking-[0.15em] px-4 py-2 border border-brand-gray-200 rounded-full hover:bg-brand-gray-50 transition-all"><i className="ri-printer-line mr-1"/> Imprimir</button>
            <button onClick={handleDownloadPDF} disabled={pdfGenerating} className="text-[10px] uppercase tracking-[0.15em] px-4 py-2 border border-brand-gray-200 rounded-full hover:bg-brand-gray-50 transition-all disabled:opacity-30">
              {pdfGenerating ? '...' : 'PDF'}
            </button>
            <button onClick={handleGenerateImage} disabled={imgGenerating || !libsReady} className="text-[10px] uppercase tracking-[0.15em] px-5 py-2 bg-black text-white rounded-full hover:bg-brand-gray-800 transition-all disabled:opacity-30 flex items-center gap-1">
              <i className="ri-printer-line"/>{imgGenerating ? 'Generando...' : 'Enviar a impresora'}
            </button>
          </div>
        </div>

        {/* TICKET FORMAT RECEIPT */}
        <div className="w-full flex justify-center pb-8">
          <div
            id="ticket"
            ref={receiptRef}
            className="bg-white relative p-8 border border-brand-gray-100 shadow-xl mx-auto flex flex-col items-center"
            style={{ width: '300px', minWidth: '300px' }}
          >
            {/* Header */}
            <img src={SITE_LOGO} alt="Anthony Sánchez" className="h-6 w-auto mb-4 opacity-90" />
            <h1 className="text-[10px] uppercase tracking-[0.3em] font-bold text-center border-b border-dashed border-brand-gray-300 w-full pb-4 mb-4">
              Recibo de Arreglo
            </h1>

            {/* General Info */}
            <div className="w-full space-y-3 mb-6 text-xs text-center text-brand-black">
              <p className="font-mono text-[9px] text-brand-gray-400">Nº {alteration.id.split('-')[0].toUpperCase()}</p>
              <p>{new Date(alteration.created_at).toLocaleString()}</p>
              <p className="font-bold text-sm uppercase mt-2">{alteration.partners?.name}</p>
            </div>

            {/* Client Info */}
            <div className="w-full border-y border-dashed border-brand-gray-300 py-4 mb-6 space-y-1">
              <p className="text-[9px] uppercase tracking-widest text-brand-gray-400 font-bold mb-2">Datos del Cliente</p>
              <p className="text-sm font-bold capitalize">{alteration.client_name}</p>
              <p className="text-xs text-brand-gray-500">{alteration.client_phone || 'Sin teléfono'}</p>
            </div>

            {/* Service Details */}
            <div className="w-full space-y-3 mb-6">
              <p className="text-[9px] uppercase tracking-widest text-brand-gray-400 font-bold">Servicio</p>
              <div>
                <p className="font-bold text-sm">{alteration.service_name || 'Servicio General'}</p>
                <p className="text-xs text-brand-gray-500 mt-1">{alteration.description}</p>
              </div>
            </div>

            {/* Total Price */}
            <div className="w-full border-t border-brand-gray-800 pt-4 mb-8 flex justify-between items-end">
              <span className="text-[10px] uppercase tracking-widest font-bold">Total a Pagar</span>
              <span className="text-2xl font-serif font-bold tracking-tight">{alteration.price.toFixed(2)}€</span>
            </div>

            {/* Footer */}
            <div className="text-center w-full space-y-2">
              <p className="text-[8px] uppercase tracking-[0.2em] text-brand-gray-400">Gracias por confiar en</p>
              <p className="text-[9px] uppercase tracking-[0.3em] font-bold">Anthony Sánchez</p>
              <p className="text-[7px] uppercase tracking-[0.1em] text-brand-gray-300 mt-2">Atelier de Alta Costura</p>
            </div>
          </div>
        </div>
      </div>

      {/* Modal: imagen del ticket para imprimir desde la app POS-Printer */}
      {shareImageUrl && (
        <div onClick={closeShareModal} className="fixed inset-0 z-50 bg-black/80 flex items-center justify-center p-6 no-print">
          <div onClick={(e) => e.stopPropagation()} className="bg-white rounded-3xl p-5 w-full max-w-[320px] flex flex-col items-center max-h-[90vh] overflow-y-auto">
            <p className="text-[11px] uppercase tracking-[0.2em] text-brand-gray-400 font-bold mb-3 text-center">Imprimir con POS-Printer</p>
            <img src={shareImageUrl} alt="Ticket" className="w-40 h-auto border border-brand-gray-100 rounded-lg mb-4" />
            <p className="text-xs text-brand-gray-500 text-center mb-4 leading-relaxed">
              Pulsa <b>Guardar / Enviar</b> para guardar la imagen en <b>Fotos</b> (o mantén pulsada la imagen → “Añadir a Fotos”). Luego ábrela desde la app <b>POS-Printer</b> y selecciónala para imprimir.
            </p>
            <div className="flex gap-3 w-full">
              <button onClick={closeShareModal} className="flex-1 px-4 py-2.5 rounded-full text-sm text-brand-gray-500 hover:bg-brand-gray-100 transition-colors">Cerrar</button>
              <button onClick={handleShareFile} className="flex-1 px-4 py-2.5 rounded-full text-sm bg-black text-white hover:bg-brand-gray-800 transition-colors flex items-center justify-center gap-1">
                <i className="ri-share-line"/> Guardar / Enviar
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
